import type { CartProductOptionSelection, CartProductOptions, ProductOptionGroup } from '@/services/productService'

export function parseOrderLineOptions(raw: unknown): CartProductOptionSelection[] {
  if (raw == null || typeof raw !== 'object') return []
  const po = (raw as Record<string, unknown>).product_options
  if (!po || typeof po !== 'object' || Array.isArray(po)) return []
  const selections = (po as { selections?: CartProductOptionSelection[] }).selections
  return Array.isArray(selections) ? selections : []
}

export function orderLineOptionsSummary(raw: unknown): string | undefined {
  const selections = parseOrderLineOptions(raw)
  if (!selections.length) return undefined
  return selections
    .map((s) => {
      const adj = Number(s.price_adjustment) || 0
      const adjPart =
        adj > 0 ? ` (+$${adj.toFixed(2)})` : adj < 0 ? ` (-$${Math.abs(adj).toFixed(2)})` : ''
      return `${s.option_group_name}: ${s.label}${adjPart}`
    })
    .join('; ')
}

export function extractOrderLineOptionsSignature(raw: unknown): string {
  if (raw == null || typeof raw !== 'object') return ''
  const po = (raw as Record<string, unknown>).product_options
  if (po && typeof po === 'object' && !Array.isArray(po)) {
    const sig = (po as Record<string, unknown>).signature
    if (typeof sig === 'string') return sig
  }
  return ''
}

export function buildOptionsSignature(selectionMap: Record<number, number>): string {
  return Object.keys(selectionMap)
    .map((k) => Number(k))
    .filter((n) => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b)
    .map((gid) => `${gid}:${selectionMap[gid]}`)
    .join('|')
}

export function buildOrderLineOptionsPayload(
  groups: ProductOptionGroup[],
  selectionMap: Record<number, number>,
): CartProductOptions {
  const selections: CartProductOptionSelection[] = []
  for (const group of groups) {
    const itemId = selectionMap[group.option_group_id]
    if (itemId == null) continue
    const item = group.items.find((i) => i.option_item_id === itemId)
    if (!item) continue
    selections.push({
      option_group_id: group.option_group_id,
      option_group_name: group.name,
      option_item_id: item.option_item_id,
      label: item.label,
      price_adjustment: Number(item.price_adjustment) || 0,
    })
  }
  return {
    product_options: {
      signature: buildOptionsSignature(selectionMap),
      selections,
    },
  }
}

export function orderLineProductKey(productId: number, options: unknown): string {
  return `${productId}:${extractOrderLineOptionsSignature(options)}`
}

export function computeOptionPriceAdjustment(
  groups: ProductOptionGroup[],
  selectionMap: Record<number, number>,
): number {
  let total = 0
  for (const group of groups) {
    const itemId = selectionMap[group.option_group_id]
    const item = group.items.find((i) => i.option_item_id === itemId)
    if (item) total += Number(item.price_adjustment) || 0
  }
  return total
}
